/**
 * Async data-loading primitives.
 *
 * The legacy code called `window.api.request(...)` directly inside render
 * functions, with no cancellation, so a fast tab switch could write a stale
 * response over a fresh one. These hooks make that class of bug impossible.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Run an async function and track its lifecycle.
 *
 * @param {Function} asyncFn        Receives no arguments; close over what it needs.
 * @param {Array}    deps           Re-runs when these change.
 * @param {object}   [options]
 * @param {any}      [options.initialData]
 * @param {boolean}  [options.immediate=true]  Run on mount.
 * @param {boolean}  [options.enabled=true]    Skip entirely when false.
 */
export function useAsync(asyncFn, deps = [], options = {}) {
  const { initialData = null, immediate = true, enabled = true } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(Boolean(immediate && enabled));
  const [error, setError] = useState(null);

  // Only the newest run is allowed to commit — this is the stale-write guard.
  const runId = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const execute = useCallback(async () => {
    if (!enabled) return null;

    runId.current += 1;
    const thisRun = runId.current;

    setLoading(true);
    setError(null);

    try {
      const result = await asyncFn();
      if (mounted.current && thisRun === runId.current) {
        setData(result);
        setLoading(false);
      }
      return result;
    } catch (err) {
      if (mounted.current && thisRun === runId.current) {
        setError(err);
        setLoading(false);
      }
      return null;
    }
    // asyncFn is intentionally excluded: callers pass inline closures, and deps
    // is the explicit re-run contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  useEffect(() => {
    if (immediate && enabled) execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execute, immediate, enabled]);

  return { data, loading, error, execute, reload: execute, setData };
}

/**
 * Run an async action on demand (form submits, button clicks).
 * Tracks `submitting` and the last error so components do not each re-invent it.
 */
export function useAsyncAction(action) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (...args) => {
      setSubmitting(true);
      setError(null);
      try {
        const result = await action(...args);
        if (mounted.current) setSubmitting(false);
        return { ok: true, data: result, error: null };
      } catch (err) {
        if (mounted.current) {
          setError(err);
          setSubmitting(false);
        }
        return { ok: false, data: null, error: err };
      }
    },
    [action],
  );

  return { run, submitting, error, clearError: () => setError(null) };
}
